import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { safeFetch } from "../utils/api";
import CopyableContainer from "./CopyableContainer";
import PendingDataNotice from "./PendingDataNotice";
import LeagueTabHeading from "./LeagueTabHeading";
import LeagueTabSkeleton from "./LeagueTabSkeleton";
import PlayoffTableImageCopyButton from "./PlayoffTableImageCopyButton";
import { remainingScheduleImageOptions } from "../utils/playoffTableImage";
import { probabilityHeatStyle } from "../utils/simulationHeatmap";
import "./styles/tableStyles.css";

const scheduleMetricKeys = [
    "opp_points_for",
    "opp_win_pct",
    "opp_power_rank",
    "overall_difficulty",
];

const normalizeScheduleMetric = (value, range) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || !range) return 0;
    if (range.max === range.min) return 0.5;

    return (numericValue - range.min) / (range.max - range.min);
};

const RemainingStrengthOfScheduleTable = ({
    provider,
    leagueId,
    leagueYear,
    week,
    nCompletedWeeks,
}) => {
    const [
        remainingStrengthOfScheduleData,
        setRemainingStrengthOfScheduleData,
    ] = useState(null);
    const [minWeek, setMinWeek] = useState(null);
    const [maxWeek, setMaxWeek] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const navigate = useNavigate(); // Add navigation hook

    if (fetchError) {
        throw fetchError;
    }

    // Fetch remaining strength of schedule data
    useEffect(() => {
        const fetchRemainingStrengthOfScheduleData = (week) => {
            const endpoint = `/api/remaining-strength-of-schedule/${provider}/${leagueYear}/${leagueId}/?week=${week}`;

            safeFetch(endpoint, {}, false, 2)
                .then((data) => {
                    if (data?.redirect) {
                        console.log(`Redirecting to: ${data.redirect}`);
                        navigate(data.redirect);
                    } else {
                        setRemainingStrengthOfScheduleData(
                            data.remaining_strength_of_schedule
                        );
                        setMinWeek(data.min_week > 0 ? data.min_week : null);
                        setMaxWeek(data.max_week > 0 ? data.max_week : null);
                        console.log(
                            "Remaining strength of schedule data fetched:",
                            data
                        );
                    }
                })
                .catch((err) => {
                    console.error("ERROR:", endpoint, err);
                    setFetchError(err);
                });
        };

        if (leagueYear && leagueId && week) {
            fetchRemainingStrengthOfScheduleData(week);
        }
    }, [provider, leagueYear, leagueId, week]);

    console.log("minWeek:", minWeek);
    console.log("maxWeek:", maxWeek);

    const title = `Remaining Strength of Schedule${
        minWeek && maxWeek
            ? minWeek === maxWeek
                ? ` for Week ${minWeek}` // Show only one week if minWeek equals maxWeek
                : ` for Weeks ${minWeek}-${maxWeek}` // Show range if minWeek and maxWeek are different
            : ""
    }`;

    const fileName = `remaining-strength-of-schedule${
        minWeek && maxWeek
            ? minWeek === maxWeek
                ? `-week-${minWeek}`
                : `-weeks-${minWeek}-${maxWeek}`
            : ""
    }`;

    if (remainingStrengthOfScheduleData === null) {
        return <LeagueTabSkeleton variant="remaining-schedule" label="Calculating remaining strength of schedule" />;
    }

    const metricRanges = scheduleMetricKeys.reduce((ranges, key) => {
        const values = remainingStrengthOfScheduleData
            .map((team) => Number(team[key]))
            .filter(Number.isFinite);

        ranges[key] = values.length
            ? { min: Math.min(...values), max: Math.max(...values) }
            : null;
        return ranges;
    }, {});

    const scheduleHeatStyle = (team, key) =>
        probabilityHeatStyle(normalizeScheduleMetric(team[key], metricRanges[key]));

    return (
        <CopyableContainer
            title={
                <div className="rank-distribution-heading">
                    <LeagueTabHeading title={title} />
                    <PlayoffTableImageCopyButton
                        imageOptions={remainingScheduleImageOptions({
                            data: remainingStrengthOfScheduleData,
                            title,
                        })}
                        fileName={fileName}
                        disabled={!Array.isArray(remainingStrengthOfScheduleData)}
                    />
                </div>
            }
            fileName={fileName}
            className="simulation-copyable-shell playoff-table-image-copyable remaining-schedule-copyable"
        >
            <PendingDataNotice
                dataType="Remaining Strengths of Schedule"
                isPending={week > nCompletedWeeks}
            />
            <div className="table-rounded-shell">
            <table className="table-with-bottom-caption remaining-schedule-table">
                <thead>
                    <tr>
                        <th className="team-name-column">Team</th>
                        <th className="team-name-column">Owner</th>
                        <th className="remaining-schedule-metric-column">
                            Opponent<br />Points For
                        </th>
                        <th className="remaining-schedule-metric-column">
                            Opponent<br />Win %
                        </th>
                        <th className="remaining-schedule-metric-column">
                            Opponent<br />Power Rank
                        </th>
                        <th className="remaining-schedule-metric-column remaining-schedule-overall-column">
                            Overall<br />Difficulty
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {remainingStrengthOfScheduleData.map((team, index) => (
                        <tr
                            key={index}
                            className={
                                index % 2 === 0 ? "even-row" : "odd-row"
                            }
                        >
                            <td className="team-name-column">
                                {team.team}
                            </td>
                            <td className="team-name-column">
                                {team.owner}
                            </td>
                            <td className="remaining-schedule-metric-column simulation-heat-cell">
                                <div className="simulation-heat" style={scheduleHeatStyle(team, "opp_points_for")}>
                                    {team.opp_points_for.toFixed(2)}
                                </div>
                            </td>
                            <td className="remaining-schedule-metric-column simulation-heat-cell">
                                <div className="simulation-heat" style={scheduleHeatStyle(team, "opp_win_pct")}>
                                    {team.opp_win_pct.toFixed(3)}
                                </div>
                            </td>
                            <td className="remaining-schedule-metric-column simulation-heat-cell">
                                <div className="simulation-heat" style={scheduleHeatStyle(team, "opp_power_rank")}>
                                    {team.opp_power_rank.toFixed(1)}
                                </div>
                            </td>
                            <td className="remaining-schedule-metric-column remaining-schedule-overall-column simulation-heat-cell">
                                <div className="simulation-heat" style={scheduleHeatStyle(team, "overall_difficulty")}>
                                    {team.overall_difficulty.toFixed(1)}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </CopyableContainer>
    );
};

RemainingStrengthOfScheduleTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            team: PropTypes.string.isRequired,
            owner: PropTypes.string.isRequired,
            opp_points_for: PropTypes.string.isRequired,
            opp_win_pct: PropTypes.string.isRequired,
            opp_power_rank: PropTypes.string.isRequired,
            overall_difficulty: PropTypes.string.isRequired,
        })
    ),
};

export default RemainingStrengthOfScheduleTable;
